import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect } from 'react';
import { extractRawText } from 'mammoth';
import { Button, useStream } from '@airia/ui';
import { Drawer, DrawerContent, DrawerHeader, DrawerTrigger } from '../../ui/drawer';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '../../ui/dialog';
import { Plus, X } from 'lucide-react';
import { ProfileDropdown } from './ProfileDropdown';
import { SignalRStatusDot } from '../SignalRStatusDot';
import UploadFile from './UploadFile';
import { getAddInPlaybookDocCompareDeployment, getAddInPlaybookRulesExtractDeployment, } from '../../services/agent';
import { isAxiosError } from 'axios';
import PlaybookList from './PlaybookList';
import PlaybookDetails from './PlaybookDetails';
import { useAuth } from 'react-oidc-context';
import { searchText } from '../../lib/office';
import { getRequiredConfig } from '../../lib/service-discovery';
import { useUserId } from '../../lib/useUserId';
import { useBrowserAgentVariables } from '../McpSessionProvider';
const UploadPlaybook = () => {
    const { user } = useAuth();
    const { apiUrl } = getRequiredConfig();
    const { userId } = useUserId();
    const browserAgentVariables = useBrowserAgentVariables();
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [playbooks, setPlaybooks] = React.useState([]);
    const [playbookIdToUpdate, setPlaybookIdToUpdate] = React.useState(null);
    const [selectedPlaybook, setSelectedPlaybook] = React.useState(null);
    const [playbookRulesExtractDeployment, setPlaybookRulesExtractDeployment] = React.useState('');
    const [playbookCompareDeployment, setPlaybookCompareDeployment] = React.useState('');
    const [playbookToDelete, setPlaybookToDelete] = React.useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const { messages, submit } = useStream({
        apiUrl,
        agentId: playbookRulesExtractDeployment,
        bearerToken: user?.access_token,
    });
    useEffect(() => {
        getAddInPlaybookRulesExtractDeployment(user?.access_token ?? '')
            .then((deployment) => {
            console.log('PlaybookRulesExtract deployment:', deployment);
            setPlaybookRulesExtractDeployment(deployment.pipelineId);
        })
            .catch((error) => {
            console.error('Failed to get PlaybookRulesExtract deployment:', error);
        });
        getAddInPlaybookDocCompareDeployment(user?.access_token ?? '')
            .then((deployment) => {
            console.log('PlaybookDocCompare deployment:', deployment);
            setPlaybookCompareDeployment(deployment.pipelineId);
        })
            .catch((error) => {
            console.error('Failed to get PlaybookDocCompare deployment:', error);
        });
        const savedPlaybooks = localStorage.getItem(`${user?.profile.sub}:playbooks`);
        if (savedPlaybooks) {
            setPlaybooks(JSON.parse(savedPlaybooks));
        }
    }, [user?.access_token, user?.profile.sub]);
    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant' && playbookIdToUpdate) {
            setPlaybooks((prev) => {
                const index = prev.findIndex((p) => p.id === playbookIdToUpdate);
                if (index !== -1) {
                    prev[index] = {
                        ...prev[index],
                        status: 'Completed',
                        clauses: lastMessage.value,
                    };
                    localStorage.setItem(`${user?.profile.sub}:playbooks`, JSON.stringify(prev));
                }
                return [...prev];
            });
        }
    }, [messages, playbookIdToUpdate, user?.profile.sub]);
    const onFileAttach = async (file) => {
        setDrawerOpen(false);
        const playbookId = `${Math.random()}`;
        const playbook = {
            id: playbookId,
            name: file.name,
            status: 'Processing',
            clauses: '',
            comparisons: [],
        };
        setPlaybookIdToUpdate(playbook.id);
        setPlaybooks((prev) => [...prev, playbook]);
        try {
            const text = await extractRawText({ arrayBuffer: await file.arrayBuffer() });
            if (!userId) {
                throw new Error('User ID not available');
            }
            await submit({
                data: {
                    userInput: text.value,
                    userId,
                    variables: browserAgentVariables,
                },
            });
        }
        catch (error) {
            playbook.status = 'Failed';
            setPlaybooks((prev) => {
                const index = prev.findIndex((p) => p.id === playbookId);
                prev[index] = playbook;
                return [...prev];
            });
            if (isAxiosError(error)) {
                console.error('error executing the pipeline', error.response?.data);
            }
            else {
                console.error('error parsing the document', error);
            }
        }
    };
    const onPlaybookClick = (playbook) => {
        setSelectedPlaybook(playbook);
    };
    const onComparisonPipelineExecution = useCallback(async (playbookId, comparisons) => {
        setPlaybooks((prev) => {
            const index = prev.findIndex((p) => p.id === playbookId);
            if (index === -1)
                return prev;
            // Create a new array with a new object to avoid mutation
            const updated = [...prev];
            updated[index] = { ...updated[index], comparisons };
            if (comparisons.length > 0) {
                searchText(comparisons[0].original);
            }
            return updated;
        });
        // Also update selectedPlaybook so the UI reflects the new comparisons
        setSelectedPlaybook((prev) => {
            if (prev && prev.id === playbookId) {
                return { ...prev, comparisons };
            }
            return prev;
        });
    }, []);
    const handleDeleteClick = (playbook) => {
        setPlaybookToDelete(playbook);
        setShowDeleteConfirm(true);
    };
    const confirmDelete = () => {
        if (playbookToDelete) {
            const updatedPlaybooks = playbooks.filter((p) => p.id !== playbookToDelete.id);
            setPlaybooks(updatedPlaybooks);
            localStorage.setItem(`${user?.profile.sub}:playbooks`, JSON.stringify(updatedPlaybooks));
        }
        setShowDeleteConfirm(false);
        setPlaybookToDelete(null);
    };
    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setPlaybookToDelete(null);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Dialog, { open: showDeleteConfirm, onOpenChange: setShowDeleteConfirm, children: _jsxs(DialogContent, { children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Delete Playbook" }), _jsxs(DialogDescription, { children: ["Are you sure you want to delete \"", playbookToDelete?.name, "\"? This action cannot be undone."] })] }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", onClick: cancelDelete, children: "Cancel" }), _jsx(Button, { variant: "destructive", onClick: confirmDelete, children: "Delete" })] })] }) }), _jsxs(Drawer, { open: drawerOpen, onOpenChange: setDrawerOpen, children: [_jsxs("div", { className: "h-full flex flex-col bg-background", children: [_jsxs("div", { className: 'w-full flex justify-between items-center p-4 flex-shrink-0', children: [_jsx("div", { className: 'flex gap-4 items-center', children: _jsx("h1", { className: 'text-xl', children: `Playbooks` }) }), _jsxs("div", { className: 'flex gap-2', children: [_jsx(DrawerTrigger, { "data-testid": "playbook-upload-trigger", children: _jsx(Button, { variant: 'ghost', size: 'icon', children: _jsx(Plus, {}) }) }), _jsx(SignalRStatusDot, {}), _jsx(ProfileDropdown, {})] })] }), _jsx("div", { className: "flex-1 min-h-0 w-full", children: selectedPlaybook ? (_jsx(PlaybookDetails, { playbookDocComparisonDeploymentId: playbookCompareDeployment, playbook: selectedPlaybook, onBack: () => setSelectedPlaybook(null), onComparisonPipelineExecution: onComparisonPipelineExecution })) : (_jsx(PlaybookList, { playbooks: playbooks, onPlaybookClick: onPlaybookClick, onDeletePlaybook: handleDeleteClick })) })] }), _jsxs(DrawerContent, { className: 'h-[50vh] bg-background p-4', children: [_jsx(DrawerHeader, { children: _jsxs("div", { className: 'flex justify-between items-center', children: [_jsx("h1", { children: `Upload Playbooks` }), _jsx(Button, { variant: 'ghost', size: 'icon', onClick: () => setDrawerOpen(false), className: 'text-muted-foreground', children: _jsx(X, {}) })] }) }), _jsx(UploadFile, { onFileAttach: onFileAttach })] })] })] }));
};
export default UploadPlaybook;
